import { useState, useCallback, useRef } from 'react';
import { ChordInspectorData, AppMode } from '../types';
import { parseChordToNotes } from '../utils/audio';

interface ChordInspectorDeps {
  mode: AppMode;
  uiSettings: { inspectorEnabled: boolean };
}

export function useChordInspector(deps: ChordInspectorDeps) {
  const { mode, uiSettings } = deps;

  const [inspectorData, setInspectorData] = useState<ChordInspectorData | null>(null);
  const [isInspectorHovered, setIsInspectorHovered] = useState(false);
  const [isInspectorPinned, setIsInspectorPinned] = useState(false);
  const inspectorCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inspectorAutoCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleChordHover = useCallback((chordString: string, element: HTMLElement) => {
    if (inspectorCloseTimeoutRef.current) {
      clearTimeout(inspectorCloseTimeoutRef.current);
      inspectorCloseTimeoutRef.current = null;
    }

    if (mode !== 'edition' || !chordString.trim() || !uiSettings.inspectorEnabled) {
      setInspectorData(null);
      setIsInspectorPinned(false);
      return;
    }

    const parsed = parseChordToNotes(chordString);
    if (parsed.error || !parsed.notes || !parsed.rootNote) {
      setInspectorData(null);
      setIsInspectorPinned(false);
      return;
    }

    const rect = element.getBoundingClientRect();
    setInspectorData({
      chord: chordString,
      notes: parsed.notes,
      rootNote: parsed.rootNote,
      bassNote: parsed.bassNote,
      position: {
        x: rect.left + rect.width / 2,
        y: rect.top,
      },
    });

    const isMobileOrTablet = window.innerWidth <= 1024 || 'ontouchstart' in window;
    if (isMobileOrTablet && !isInspectorPinned) {
      if (inspectorAutoCloseTimeoutRef.current) {
        clearTimeout(inspectorAutoCloseTimeoutRef.current);
      }
      inspectorAutoCloseTimeoutRef.current = setTimeout(() => {
        if (!isInspectorPinned) {
          setInspectorData(null);
        }
        inspectorAutoCloseTimeoutRef.current = null;
      }, 5000);
    }
  }, [mode, uiSettings.inspectorEnabled, isInspectorPinned]);

  const handleChordLeave = useCallback(() => {
    if (inspectorCloseTimeoutRef.current) {
      clearTimeout(inspectorCloseTimeoutRef.current);
    }

    inspectorCloseTimeoutRef.current = setTimeout(() => {
      if (!isInspectorHovered) {
        setInspectorData(null);
      }
      inspectorCloseTimeoutRef.current = null;
    }, 500);
  }, [isInspectorHovered]);

  const cancelAutoClose = useCallback(() => {
    if (inspectorAutoCloseTimeoutRef.current) {
      clearTimeout(inspectorAutoCloseTimeoutRef.current);
      inspectorAutoCloseTimeoutRef.current = null;
    }
  }, []);

  const cancelCloseTimeout = useCallback(() => {
    if (inspectorCloseTimeoutRef.current) {
      clearTimeout(inspectorCloseTimeoutRef.current);
      inspectorCloseTimeoutRef.current = null;
    }
  }, []);

  return {
    inspectorData,
    setInspectorData,
    isInspectorHovered,
    setIsInspectorHovered,
    isInspectorPinned,
    setIsInspectorPinned,
    handleChordHover,
    handleChordLeave,
    cancelAutoClose,
    cancelCloseTimeout,
  };
}

export type UseChordInspectorReturn = ReturnType<typeof useChordInspector>;
